import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, MapPin, Calendar as CalIcon, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ListingCard from "@/components/ListingCard";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

const CATS = [
  { key: "rehearsal", label: "Rehearsal", img: "https://images.unsplash.com/photo-1648160397611-f91f8bc6f900?w=800" },
  { key: "photo", label: "Photo / Video", img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=800" },
  { key: "meeting", label: "Meeting", img: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800" },
  { key: "fitness", label: "Fitness / Dance", img: "https://images.unsplash.com/photo-1758526387507-b27e5f7ed0fb?w=800" },
  { key: "event", label: "Event", img: "https://images.unsplash.com/photo-1519750783826-e2420f4d687f?w=800" },
  { key: "popup", label: "Pop-up", img: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=800" },
];

const MARQUEE_ITEMS = [
  "Empty coffee shops after 7pm",
  "Churches on weekday mornings",
  "Warehouses on weekends",
  "Dance studios at midnight",
  "Meeting rooms after dark",
  "Photo studios on Tuesdays",
];

export default function Landing() {
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("all");
  const [maxPrice, setMaxPrice] = useState("");
  const [email, setEmail] = useState("");
  const [notified, setNotified] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { email, city: "Indianapolis" });
      setNotified(true);
    } catch { toast.error("Couldn't save your email — try again."); }
  };
  const navigate = useNavigate();

  const { data: listings = [] } = useQuery({
    queryKey: ["home-listings"],
    queryFn: async () => (await api.get("/listings")).data,
  });

  const submit = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (category && category !== "all") params.set("category", category);
    if (maxPrice) params.set("max_price", maxPrice);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="relative h-[78vh] min-h-[560px]">
          <img
            src="https://images.unsplash.com/photo-1553668938-1b3a1e5792fc?w=2400"
            alt="Empty modern cafe"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-40 text-white">
            <div className="max-w-3xl">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-white/80 mb-6">
                SPAREHOURS / MARKETPLACE
              </div>
              <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                Every empty commercial room<br />
                <span className="text-[#FF4500]">should be earning.</span>
              </h1>
              <p className="mt-6 text-lg lg:text-xl text-white/85 max-w-xl leading-relaxed">
                Book hourly time in unused cafes, churches, studios, warehouses & conference rooms.
                Business owners list their dead hours. You book them by the hour.
              </p>
            </div>
          </div>

          {/* Floating search bar */}
          <form
            onSubmit={submit}
            className="absolute left-1/2 -translate-x-1/2 bottom-[-40px] z-20 w-[92%] max-w-[1100px] bg-white/85 backdrop-blur-2xl border border-white/60 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] p-2 grid grid-cols-2 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-2"
            data-testid="hero-search-form"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-r border-neutral-200">
              <MapPin className="w-4 h-4 text-neutral-500 shrink-0" />
              <Input
                placeholder="City — anywhere"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0 h-8 px-0 text-sm"
                data-testid="hero-city-input"
              />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-r border-neutral-200">
              <CalIcon className="w-4 h-4 text-neutral-500 shrink-0" />
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="border-0 shadow-none h-8 px-0 focus:ring-0 text-sm" data-testid="hero-category-select">
                  <SelectValue placeholder="Any activity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any activity</SelectItem>
                  {CATS.map((c) => (<SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 px-4 py-3 md:border-r border-neutral-200">
              <DollarSign className="w-4 h-4 text-neutral-500 shrink-0" />
              <Input
                type="number"
                min="0"
                placeholder="Max $ / hour"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0 h-8 px-0 text-sm"
                data-testid="hero-price-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-xl h-full min-h-14 px-6 gap-2"
              data-testid="hero-search-btn"
            >
              <SearchIcon className="w-4 h-4" /> Search
            </Button>
          </form>
        </div>
      </section>

      {/* Marquee */}
      <section className="mt-28 border-y border-neutral-200 bg-white overflow-hidden">
        <div className="marquee py-6">
          <div className="marquee__inner">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
              <div key={i} className="flex items-center gap-8 shrink-0">
                <span className="font-display font-black text-2xl md:text-3xl tracking-tight">{t}</span>
                <span className="w-2 h-2 rounded-full bg-[#FF4500]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">01 / BROWSE</div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Any room. Any hour.</h2>
          </div>
          <button
            onClick={() => navigate("/search")}
            className="text-sm font-medium underline underline-offset-4 hover:text-[#FF4500]"
            data-testid="see-all-categories"
          >
            See all listings →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CATS.map((c, i) => (
            <button
              key={c.key}
              onClick={() => navigate(`/search?category=${c.key}`)}
              className="bento-tile relative aspect-square overflow-hidden rounded-xl border border-neutral-200 group text-left"
              data-testid={`category-${c.key}`}
              style={{ gridRowStart: i % 2 === 0 ? "auto" : "auto" }}
            >
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover card-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-80">0{i + 1}</div>
                <div className="font-display font-bold text-lg leading-tight">{c.label}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* FEATURED / EMPTY STATE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">02 / RECENT</div>
            <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Fresh dead hours</h2>
          </div>
        </div>
        {listings.length === 0 ? (
          <div className="border border-neutral-200 rounded-2xl p-10 md:p-16 text-center bg-neutral-50" data-testid="empty-marketplace">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-4">NOW LAUNCHING IN INDIANAPOLIS</div>
            <h3 className="font-display font-black tracking-tighter text-3xl md:text-4xl max-w-2xl mx-auto">Spaces are coming to Indianapolis</h3>
            <p className="text-neutral-600 mt-4 max-w-xl mx-auto">We're onboarding our first SpareHours hosts right now. Own or manage an underused space?</p>
            <Button onClick={() => navigate("/list-your-space")} className="mt-6 bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-8 font-medium" data-testid="empty-founding-host">
              Become a Founding Host
            </Button>
            <div className="mt-10 pt-8 border-t border-neutral-200 max-w-md mx-auto">
              <div className="text-sm font-medium text-neutral-800">Want to know when spaces become available?</div>
              {notified ? (
                <div className="mt-3 text-sm text-[#FF4500] font-medium" data-testid="notify-success">Thanks — we'll email you when Indianapolis spaces go live.</div>
              ) : (
                <form onSubmit={submitLead} className="mt-3 flex gap-2">
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="notify-email" />
                  <Button type="submit" className="bg-[#0A0A0A] text-white hover:bg-neutral-800 rounded-full px-6 shrink-0" data-testid="notify-submit">Notify me</Button>
                </form>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.slice(0, 6).map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}
      </section>

      {/* HOST CTA */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-6">03 / FOR HOSTS</div>
            <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-[1.02]">
              Your empty hours<br />are inventory.
            </h2>
            <p className="mt-6 text-neutral-300 text-lg max-w-lg leading-relaxed">
              List your café after 7pm. Your church on weekday mornings. Your warehouse on weekends.
              We handle discovery and booking. You keep 100% of the rate you set.
            </p>
            <Button
              onClick={() => navigate("/host/new")}
              className="mt-8 bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full px-8 h-12 font-medium"
              data-testid="cta-list-space"
            >
              List a space
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "15%", v: "Renter service fee" },
              { k: "1min", v: "To list a space" },
              { k: "Indy", v: "Now launching" },
              { k: "$0", v: "To join" },
            ].map((s) => (
              <div key={s.v} className="border border-neutral-800 p-6">
                <div className="font-display font-black text-4xl">{s.k}</div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-400 mt-2">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 border-t border-neutral-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-sm bg-[#FF4500]" />
            <span className="font-display font-bold text-neutral-900">SpareHours</span>
            <span>© {new Date().getFullYear()}</span>
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-medium">
            <a href="/search" className="hover:text-[#FF4500]">Find a Space</a>
            <a href="/list-your-space" className="hover:text-[#FF4500]">List a Space</a>
            <a href="/how-it-works" className="hover:text-[#FF4500]">How It Works</a>
            <a href="/trust-safety" className="hover:text-[#FF4500]">Trust &amp; Safety</a>
            <a href="/cancellation" className="hover:text-[#FF4500]">Cancellation</a>
            <a href="/terms" className="hover:text-[#FF4500]">Terms</a>
            <a href="/privacy" className="hover:text-[#FF4500]">Privacy</a>
            <a href="/contact" className="hover:text-[#FF4500]">Contact</a>
          </nav>
        </div>
        <div className="font-mono text-xs uppercase tracking-widest text-neutral-400 mt-6">Now launching in Indianapolis</div>
      </footer>
    </div>
  );
}
